/*
 This file is part of GNU Taler
 (C) 2025 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import { CancellationToken } from "@gnu-taler/taler-util";
import { Inputs, useEffect, useRef, useState } from "preact/hooks";

export const LONG_POLL_DELAY = 15000;
const LP_MIN_DELAY = 5000;
const emptyArray: unknown[] = [];
/**
 * Call retryFn handler, the function is expected to block up to LONG_POLL_DELAY = 15000.
 * If takes longer is going to be canceled using the cancel token `ct`.
 * If the function returns a result faster a delay is added to match LP_MIN_DELAY = 5000.
 * If the function returns undefined instead of a Promise means that it was
 * not ready to do long polling and won't retry.
 *
 * @returns
 */
export function useLongPolling<Res>(
  retryFn: (
    ct: CancellationToken,
    last: undefined | Res,
    deps: Array<any>,
  ) => Promise<Res> | undefined,
  opts: {
    /**
     * Wait time in case the request returns fast. Default LP_MIN_DELAY = 5000
     */
    minTime?: number;
    /**
     * The first value of `last` in retryFn. Default undefined
     */
    initial?: Res;
    /**
     * Check before everty request if loop should stop retrying. Default always false.
     */
    shouldStop?: (res: undefined | Res) => boolean;
    /**
     * Dependency array to re-evaluate the state. Default empty.
     */
    deps?: Array<unknown>;
  } = {},
): Res | undefined {
  const minTime = opts?.minTime ?? LP_MIN_DELAY;
  const dependencies = opts?.deps ?? emptyArray;
  const [result, setResult] = useState<{
    counter: number;
    prevResult: Res | undefined;
  }>({
    counter: 0,
    prevResult: opts.initial,
  });
  const ct = useRef({ unloaded: false, startMs: 0 });
  const request = useRef({ dependencies, minTime, retryFn, opts });
  request.current = { dependencies, minTime, retryFn, opts };
  const dependencyMarker = useDependencyMarker(dependencies);
  const dependencyEffectMounted = useRef(false);

  /**
   * Retry if dependency change
   */
  useEffect(() => {
    if (!dependencyEffectMounted.current) {
      dependencyEffectMounted.current = true;
      return;
    }
    setResult((prev) => ({
      counter: prev.counter + 1,
      prevResult: opts.initial,
    }));
  }, [dependencyMarker, opts.initial]);

  /**
   * Unloading, the response is not needed anymore
   */
  useEffect(() => {
    const cancellationState = ct.current;
    return () => {
      cancellationState.unloaded = true;
      cancellationState.startMs = 0;
    };
  }, []);

  /**
   * request dependency changed
   * different resultset expected
   * cancel current request and retry ASAP
   */

  useEffect(() => {
    const tk = CancellationToken.create();
    const {
      dependencies: currentDependencies,
      minTime: currentMinTime,
      opts: currentOptions,
      retryFn: currentRetryFn,
    } = request.current;
    const previousResult = result.prevResult;

    if (currentOptions.shouldStop?.(previousResult)) return;

    const diff = new Date().getTime() - ct.current.startMs;
    const instant = Promise.resolve();
    const enoughTimePassed = diff > currentMinTime;

    const do_wait = (
      enoughTimePassed || previousResult === undefined
        ? instant
        : delayMs(currentMinTime - diff)
    ).then((_) => false);

    do_wait.then(() => {
      if (ct.current.unloaded || tk.token.isCancelled) return;
      const p = currentRetryFn(tk.token, previousResult, currentDependencies);
      if (p) {
        ct.current.startMs = new Date().getTime();
        Promise.race([p, cancelOnTimeout(LONG_POLL_DELAY + 100, tk)])
          .then((prevResult) => {
            setResult((prev) => ({
              counter: prev.counter + 1,
              prevResult,
            }));
          })
          .catch((error: unknown) => {
            const reason =
              error instanceof CancellationToken.CancellationError
                ? error.reason
                : error instanceof Error
                  ? error.message
                  : error;
            if (reason === "long-polling-stop" || ct.current.unloaded) return;
            if (reason !== "long-polling-timeout") {
              console.error("Long polling error", error);
            }
            setResult((prev) => ({
              counter: prev.counter + 1,
              prevResult: previousResult,
            }));
          });
      }
    });
    return () => {
      tk.cancel("long-polling-stop");
    };
  }, [result.counter, result.prevResult]);

  return result.prevResult;
}

/**
 * this should be in taler-utils
 * @param ms
 * @returns
 */
export async function delayMs(
  ms: number,
  ct?: CancellationToken,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let unregister: () => void = () => undefined;
    const timer = setTimeout(() => {
      unregister();
      resolve();
    }, ms);
    if (ct) {
      unregister = ct.onCancelled((reason) => {
        clearTimeout(timer);
        unregister();
        reject(new CancellationToken.CancellationError(reason));
      });
    }
  });
}
async function cancelOnTimeout(ms: number, tk: CancellationToken.Source) {
  return delayMs(ms).then((_) => {
    tk.cancel("long-polling-timeout");
    throw new CancellationToken.CancellationError("long-polling-timeout");
  });
}
function useDependencyMarker(inputs: Inputs | undefined): object {
  const current = useRef<{ inputs: Inputs; marker: object }>();
  const nextInputs = inputs ?? emptyArray;
  if (
    !current.current ||
    current.current.inputs.length !== nextInputs.length ||
    current.current.inputs.some(
      (previous, index) => !Object.is(previous, nextInputs[index]),
    )
  ) {
    current.current = { inputs: nextInputs, marker: {} };
  }
  return current.current.marker;
}
